import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, IndianRupee, Save, Send, TriangleAlert } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SecureImage } from "@/components/SecureImage";
import { ProductImageEditor } from "@/components/admin/ProductImageEditor";
import { api, formatApiErrorDetail } from "@/lib/api";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function ReviewGrid({ batch, products, onRefresh }) {
  const [rows, setRows] = useState(products);
  const [savingId, setSavingId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get("/admin/categories");
      setCategoryOptions(response.data);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  useEffect(() => setRows(products), [products]);
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const updateRow = (id, field, value) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      const matchedCategory = categoryOptions.find((option) => option.name === row.category);
      let finalCategory = row.category || null;
      const renamedTo = (row.categoryRename ?? "").trim();
      const isRename = Boolean(matchedCategory && renamedTo && renamedTo !== matchedCategory.name);
      if (isRename) {
        const renameResponse = await api.patch(`/admin/categories/${matchedCategory.id}`, { name: renamedTo });
        finalCategory = renameResponse.data.name;
        toast.success(`Category renamed to "${finalCategory}" — all products updated`);
      }
      const payload = {
        name: row.name,
        description: row.description,
        colors: Array.isArray(row.colors) ? row.colors : String(row.colors || "").split(",").map((color) => color.trim()).filter(Boolean),
        vendor_base_cost: Number(row.vendor_base_cost || 0),
        margin_percent: Number(row.margin_percent || 40),
        transport_cost: Number(row.transport_cost || 0),
        gst_rate: Number(row.gst_rate || 0),
        minimum_quantity: Math.max(1, Math.floor(Number(row.minimum_quantity) || 1)),
        hsn_code: row.hsn_code,
        category: finalCategory,
        approved: Boolean(row.approved),
      };
      const response = await api.patch(`/admin/products/${row.id}`, payload);
      setRows((current) => current.map((item) => (item.id === row.id ? { ...response.data, categoryRename: undefined } : item)));
      toast.success("Staged product recalculated");
      if (isRename) {
        await loadCategories();
        onRefresh();
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const publish = async () => {
    if (!batch) return;
    setPublishing(true);
    try {
      const response = await api.post(`/admin/imports/${batch.id}/publish`);
      toast.success(`Published ${response.data.published_products} products and ${response.data.published_banners} banners`);
      onRefresh();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  if (!batch) {
    return (
      <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-8 text-center text-slate-400" data-testid="spreadsheet-grid-container">
        Select an import batch to review extracted products.
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/80 shadow-xl" data-testid="spreadsheet-grid-container">
      <div className="sticky top-0 z-10 flex flex-col gap-4 border-b border-slate-800 bg-slate-950/90 p-4 backdrop-blur-md lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="font-headline text-xl font-semibold text-white">Admin review grid</h3>
          <p className="text-sm text-slate-400">MOQ locked to 50 pcs. Default margin 40%. Final price includes GST and transport.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" data-testid="quantity-filter-summary">
            {rows.filter((row) => row.quantity_filtered).length} quantity filtered
          </Badge>
          <Badge className="border-blue-500/30 bg-blue-500/10 text-blue-300" data-testid="moq-rule-badge">MOQ 50 pcs</Badge>
          <Button data-testid="approve-publish-button" onClick={publish} disabled={publishing || rows.length === 0} className="bg-emerald-500 text-white hover:bg-emerald-400">
            <Send className="h-4 w-4" /> {publishing ? "Publishing..." : "Approve & Publish"}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-[1700px] border-collapse">
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">Image</TableHead>
              <TableHead className="text-slate-400">Particulars</TableHead>
              <TableHead className="text-slate-400">Colors</TableHead>
              <TableHead className="text-slate-400">SKU</TableHead>
              <TableHead className="text-slate-400">Category</TableHead>
              <TableHead className="text-slate-400">HSN</TableHead>
              <TableHead className="text-slate-400">Sample</TableHead>
              <TableHead className="text-slate-400">Vendor cost</TableHead>
              <TableHead className="text-slate-400">Margin %</TableHead>
              <TableHead className="text-slate-400">GST %</TableHead>
              <TableHead className="text-slate-400">Transport</TableHead>
              <TableHead className="text-slate-400">MOQ</TableHead>
              <TableHead className="text-slate-400">Min Qty</TableHead>
              <TableHead className="text-slate-400">Final price</TableHead>
              <TableHead className="text-slate-400">Flags</TableHead>
              <TableHead className="text-slate-400">Approved</TableHead>
              <TableHead className="text-slate-400">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} className="py-10 text-center text-slate-500" data-testid="empty-review-grid-state">
                  {batch.status === "processing" ? "AI extraction is processing..." : "No staged products extracted yet."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} className={`border-slate-800 ${row.quantity_filtered ? "bg-amber-500/5" : "hover:bg-slate-800/40"}`} data-testid={`review-row-${row.id}`}>
                  <TableCell>
                    {row.images?.[0]?.file_id ? (
                      <SecureImage fileId={row.images[0].file_id} alt={row.name} className="h-16 w-16 rounded-lg border border-slate-700 object-cover" />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-600">No image</div>
                    )}
                  </TableCell>
                  <TableCell className="min-w-72">
                    <Input data-testid={`editable-cell-particulars-${row.id}`} value={row.name || ""} onChange={(event) => updateRow(row.id, "name", event.target.value)} className="mb-2 border-slate-700 bg-slate-950 text-slate-100" />
                    <Input value={row.description || ""} onChange={(event) => updateRow(row.id, "description", event.target.value)} className="border-slate-800 bg-slate-950 text-xs text-slate-400" />
                  </TableCell>
                  <TableCell>
                    <Input value={Array.isArray(row.colors) ? row.colors.join(", ") : row.colors || ""} onChange={(event) => updateRow(row.id, "colors", event.target.value)} className="w-36 border-slate-700 bg-slate-950 text-slate-100" />
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs text-blue-300">{row.public_sku}</div>
                    <div className="mt-1 text-xs text-slate-500">Vendor SKU private</div>
                  </TableCell>
                  <TableCell className="min-w-52">
                    <select
                      data-testid={`category-select-${row.id}`}
                      value={row.category || ""}
                      onChange={(event) => {
                        updateRow(row.id, "category", event.target.value || null);
                        updateRow(row.id, "categoryRename", undefined);
                      }}
                      className="h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                    >
                      <option value="">Uncategorized</option>
                      {categoryOptions.map((option) => (
                        <option key={option.id} value={option.name}>{option.name}</option>
                      ))}
                      {row.category && !categoryOptions.some((option) => option.name === row.category) ? (
                        <option value={row.category}>{row.category}</option>
                      ) : null}
                    </select>
                    {categoryOptions.some((option) => option.name === row.category) ? (
                      <Input
                        data-testid={`category-rename-input-${row.id}`}
                        value={row.categoryRename ?? row.category ?? ""}
                        onChange={(event) => updateRow(row.id, "categoryRename", event.target.value)}
                        placeholder="Rename category"
                        className="mt-2 h-8 border-slate-800 bg-slate-950 text-xs text-slate-300"
                      />
                    ) : null}
                  </TableCell>
                  <TableCell><Input value={row.hsn_code || ""} onChange={(event) => updateRow(row.id, "hsn_code", event.target.value)} className="w-24 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell className="text-slate-300">{money(row.sample_price)}</TableCell>
                  <TableCell><Input data-testid={`editable-cell-price-${row.id}`} type="number" value={row.vendor_base_cost ?? ""} onChange={(event) => updateRow(row.id, "vendor_base_cost", event.target.value)} className="w-28 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell><Input data-testid={`editable-cell-margin-${row.id}`} type="number" value={row.margin_percent ?? 40} onChange={(event) => updateRow(row.id, "margin_percent", event.target.value)} className="w-24 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell><Input data-testid={`editable-cell-gst-${row.id}`} type="number" value={row.gst_rate ?? 0} onChange={(event) => updateRow(row.id, "gst_rate", event.target.value)} className="w-24 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell><Input data-testid={`editable-cell-transport-${row.id}`} type="number" value={row.transport_cost ?? 0} onChange={(event) => updateRow(row.id, "transport_cost", event.target.value)} className="w-24 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell><Badge className="border-slate-700 bg-slate-800 text-slate-200">{row.moq || 50} pcs</Badge></TableCell>
                  <TableCell><Input data-testid={`editable-cell-minimum-quantity-${row.id}`} type="number" min="1" step="1" value={row.minimum_quantity ?? 1} onChange={(event) => updateRow(row.id, "minimum_quantity", event.target.value)} className="w-24 border-slate-700 bg-slate-950 text-slate-100" /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 font-semibold text-emerald-300"><IndianRupee className="h-4 w-4" />{Number(row.retail_price || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                    <div className="mt-1 text-xs text-slate-500">incl. GST</div>
                  </TableCell>
                  <TableCell>
                    {row.quantity_filtered ? (
                      <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300"><TriangleAlert className="mr-1 h-3 w-3" />Qty 1-9 filtered</Badge>
                    ) : (
                      <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300"><CheckCircle2 className="mr-1 h-3 w-3" />Bulk ready</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <input data-testid={`approved-checkbox-${row.id}`} type="checkbox" checked={Boolean(row.approved)} onChange={(event) => updateRow(row.id, "approved", event.target.checked)} className="h-5 w-5 accent-emerald-500" />
                  </TableCell>
                  <TableCell>
                    <div className="flex min-w-36 flex-col gap-2">
                      <Button data-testid={`save-row-button-${row.id}`} size="sm" onClick={() => saveRow(row)} disabled={savingId === row.id} className="w-full bg-blue-500 text-white hover:bg-blue-400">
                        <Save className="h-4 w-4" /> {savingId === row.id ? "Saving" : "Save"}
                      </Button>
                      <ProductImageEditor product={row} onUpdated={(updated) => setRows((current) => current.map((item) => (item.id === row.id ? updated : item)))} className="w-full" />
                      <Button asChild size="sm" variant="outline" className="w-full border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800">
                        <Link data-testid={`product-details-link-${row.id}`} to={`/admin/products/${row.id}`}>Details</Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
